import type * as React from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

/**
 * True inside the pinned header's portal clone. The clone renders the SAME children as the real
 * header, so anything focusable in it would be a duplicate tab stop inside an `aria-hidden` subtree —
 * interactive headers read this to drop themselves out of the tab order there (the real header, still
 * in the document, keeps the keyboard path).
 */
const StickyCloneContext = createContext(false);

export const useIsStickyHeaderClone = () => useContext(StickyCloneContext);

function Table({ className, ...props }: React.ComponentProps<'table'>) {
    return (
        <div data-slot="table-container" className="relative w-full overflow-x-auto">
            <table data-slot="table" className={cn('w-full caption-bottom text-sm', className)} {...props} />
        </div>
    );
}

type StickyHeaderState = {
    stuck: boolean;
    top: number;
    left: number;
    width: number;
    translateX: number;
    colWidths: number[];
};

const INITIAL_STICKY_STATE: StickyHeaderState = {
    stuck: false,
    top: 0,
    left: 0,
    width: 0,
    translateX: 0,
    colWidths: [],
};

/** Walks up from `el` to find the nearest ancestor that actually scrolls vertically (e.g. AdminLayout's
 * content pane) — skipping `el` itself, since the table's own `overflow-x-auto` wrapper silently promotes
 * its own overflow-y to `auto` too and would otherwise match immediately.
 *
 * "May scroll" is not "does scroll", so the overflow style alone isn't enough: any horizontal-only
 * wrapper up the tree (a `CardContent` with `overflow-x-auto`, say) gets the same promoted `overflow-y`
 * and would match without ever scrolling vertically — pinning the header to a box that scrolls away
 * with the page, i.e. never pinning at all. The height test is what tells the two apart. */
const findScrollParent = (el: HTMLElement | null): HTMLElement | null => {
    let node = el?.parentElement ?? null;
    while (node && node !== document.body) {
        const overflowY = getComputedStyle(node).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
};

/**
 * Measures the real header against the viewport (not CSS `position: sticky`, which anchors to the
 * nearest scroll-container ancestor — the table's own `overflow-x-auto` wrapper silently promotes its
 * own overflow-y to `auto` too, becoming that ancestor, so a sticky thead would pin to a box that never
 * itself scrolls instead of the page). Driven by a capture-phase scroll listener so it fires regardless
 * of which ancestor actually scrolls (page pane, or the table's own horizontal scroll).
 *
 * The stuck header pins to the real scroll pane's own top edge, not the viewport's — on mobile that's
 * below AdminLayout's hamburger top bar (which sits outside the scroll pane and never moves); on desktop
 * there's no such chrome, so it's 0. Re-measured live so it tracks that top bar appearing/disappearing
 * across the `md` breakpoint.
 */
const useStickyHeader = (theadRef: React.RefObject<HTMLTableSectionElement | null>) => {
    const [state, setState] = useState<StickyHeaderState>(INITIAL_STICKY_STATE);

    // biome-ignore lint/correctness/useExhaustiveDependencies: theadRef is a stable ref object; re-running on its .current would defeat the mount-once listener setup
    useEffect(() => {
        const container = () => theadRef.current?.closest<HTMLElement>('[data-slot="table-container"]');

        const measure = () => {
            const theadEl = theadRef.current;
            const containerEl = container();
            if (!theadEl || !containerEl) return;

            const top = findScrollParent(containerEl)?.getBoundingClientRect().top ?? 0;
            const theadRect = theadEl.getBoundingClientRect();
            const containerRect = containerEl.getBoundingClientRect();
            const stuck = theadRect.top < top && containerRect.bottom > top + theadRect.height;
            const colWidths = Array.from(theadEl.querySelectorAll('th')).map((th) => th.getBoundingClientRect().width);

            setState({
                stuck,
                top,
                left: containerRect.left,
                width: containerRect.width,
                translateX: -containerEl.scrollLeft,
                colWidths,
            });
        };

        let raf = 0;
        const scheduleMeasure = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(measure);
        };

        measure();
        document.addEventListener('scroll', scheduleMeasure, true);
        window.addEventListener('resize', scheduleMeasure);
        const resizeObserver = new ResizeObserver(scheduleMeasure);
        resizeObserver.observe(theadRef.current!);
        const containerEl = container();
        if (containerEl) resizeObserver.observe(containerEl);

        return () => {
            cancelAnimationFrame(raf);
            document.removeEventListener('scroll', scheduleMeasure, true);
            window.removeEventListener('resize', scheduleMeasure);
            resizeObserver.disconnect();
        };
    }, []);

    return state;
};

function TableHeader({ className, children, ...props }: React.ComponentProps<'thead'>) {
    const theadRef = useRef<HTMLTableSectionElement>(null);
    const { stuck, top, left, width, translateX, colWidths } = useStickyHeader(theadRef);

    return (
        <>
            <thead ref={theadRef} data-slot="table-header" className={cn('[&_tr]:border-b', className)} {...props}>
                {children}
            </thead>
            {stuck &&
                createPortal(
                    // Clicks land on the clone whenever the header is pinned, so it has to stay
                    // interactive — a sortable header that goes dead the moment you scroll down the
                    // list is dead exactly when a long list needs it.
                    <table
                        aria-hidden="true"
                        // `m-0` cancels the base-layer `table { @apply my-6 }` in index.css, which would
                        // otherwise push this fixed clone 24px below the `top` it was measured to — a
                        // whole row of the list hiding behind the pinned header. A utility beats the base
                        // layer, so this stays a margin reset and leaves the shared prose cell styling
                        // (which keeps the clone looking identical to the real header) alone.
                        className="fixed z-30 m-0 border-collapse text-sm shadow-md"
                        style={{ top, left, width, tableLayout: 'fixed', transform: `translateX(${translateX}px)` }}
                    >
                        {colWidths.length > 0 && (
                            <colgroup>
                                {colWidths.map((colWidth, i) => (
                                    <col key={i} style={{ width: colWidth }} />
                                ))}
                            </colgroup>
                        )}
                        <thead
                            data-slot="table-header-sticky-clone"
                            className={cn('bg-background [&_tr]:border-b', className)}
                            {...props}
                        >
                            <StickyCloneContext.Provider value={true}>{children}</StickyCloneContext.Provider>
                        </thead>
                    </table>,
                    document.body,
                )}
        </>
    );
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
    return <tbody data-slot="table-body" className={cn('[&_tr:last-child]:border-0', className)} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
    return (
        <tfoot
            data-slot="table-footer"
            className={cn('bg-muted/50 border-t font-medium [&>tr]:last:border-b-0', className)}
            {...props}
        />
    );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
    return (
        <tr
            data-slot="table-row"
            className={cn('hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors', className)}
            {...props}
        />
    );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
    return (
        <th
            data-slot="table-head"
            className={cn(
                'text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
                className,
            )}
            {...props}
        />
    );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
    return (
        <td
            data-slot="table-cell"
            className={cn(
                'p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
                className,
            )}
            {...props}
        />
    );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
    return (
        <caption data-slot="table-caption" className={cn('text-muted-foreground mt-4 text-sm', className)} {...props} />
    );
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
